import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Copy,
  Mail,
  MapPin,
  Send,
  User,
  Clock,
  Users,
  Phone,
  MessageSquare,
} from "lucide-react";

/**
 * ✅ IMPORTANT
 * - 3000 = FRONT (React)
 * - 4000 = BACKEND (Express API)
 * Donc on garde API_BASE sur 4000.
 *
 * Si un jour tu ajoutes .env front, tu peux mettre:
 * REACT_APP_API_BASE_URL=http://localhost:4000
 */
const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://nouzhaback-theta.vercel.app").replace(
  /\/$/,
  ""
);

// ✅ Ton projet utilise parfois token et parfois authToken.
// On essaye plusieurs clés pour éviter les 401.
const getAuthToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("accessToken") ||
  "";

// ✅ Helpers Axios
const axiosAuth = axios.create({
  baseURL: API_BASE,
});

axiosAuth.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CircuitSurMesureManager = () => {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [prix, setPrix] = useState("");
  const [lienPaiement, setLienPaiement] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  const [commentaire, setCommentaire] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // --------- Utils ----------
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    return new Date(dateString).toLocaleDateString("fr-FR", options);
  };

  const getStatusLabel = (status) => {
    const statuses = {
      nouveau: { label: "Nouveau", class: "bg-blue-100 text-blue-800" },
      en_traitement: { label: "En traitement", class: "bg-yellow-100 text-yellow-800" },
      devis_envoye: { label: "Devis envoyé", class: "bg-green-100 text-green-800" },
      confirme: { label: "Confirmé", class: "bg-purple-100 text-purple-800" },
      annule: { label: "Annulé", class: "bg-red-100 text-red-800" },
    };
    return statuses[status] || { label: status || "—", class: "bg-slate-100 text-slate-800" };
  };

  const buildDefaultEmail = (demande, amount, paymentUrl) => {
    const clientName = demande?.prenom ? demande.prenom : demande?.nom_utilisateur || "Cher client";

    // ✅ Harmonisation devise : tu utilises MAD dans CMI, donc email en MAD.
    const amountStr =
      amount && Number(amount) > 0 ? `${Number(amount).toFixed(2)} MAD` : "[PRIX À DÉFINIR]";

    return `Bonjour ${clientName},

Nous avons le plaisir de vous communiquer le devis pour votre circuit sur mesure "${demande?.titre || ""}".

Le montant total s'élève à ${amountStr}.

Pour confirmer votre réservation, veuillez cliquer sur le lien de paiement suivant : [LIEN_PAIEMENT]

N'hésitez pas à nous contacter pour toute question.

Cordialement,
L'équipe Le Palais Touristique`.replace("[LIEN_PAIEMENT]", paymentUrl || "[LIEN_PAIEMENT]");
  };

  // --------- Load demandes ----------
  useEffect(() => {
    const fetchDemandes = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAuthToken();
        if (!token) {
          setError("Vous devez être connecté (token manquant).");
          setLoading(false);
          return;
        }

        // ✅ Ton endpoint actuel dans le code: /api/circuits-sur-mesure
        const res = await axiosAuth.get("/api/circuits-sur-mesure");

        // tolérant aux formats: { demandes }, { data: { demandes } }, etc.
        const list = res?.data?.demandes || res?.data?.data?.demandes || res?.data?.data || [];
        setDemandes(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Erreur lors du chargement des demandes:", e);
        setError("Impossible de charger les demandes. Veuillez réessayer.");
      } finally {
        setLoading(false);
      }
    };

    fetchDemandes();
  }, []);

  // --------- Open modal ----------
  const handleViewDetails = (demande) => {
    setSelectedDemande(demande);

    const currentPrix = demande?.prix ? String(demande.prix) : "";
    const currentLien = demande?.lienPaiement || "";
    const currentNotes = demande?.notes_admin || "";

    setPrix(currentPrix);
    setLienPaiement(currentLien);
    setCommentaire(currentNotes);

    setEmailMessage(buildDefaultEmail(demande, currentPrix, currentLien));
    setShowDetailsModal(true);
  };

  // ✅ URL de paiement = endpoint backend qui renvoie un HTML auto-submit vers CMI
  const computePaymentUrl = (demandeId) => `${API_BASE}/api/cmi/circuit-sur-mesure/${demandeId}`;

  // --------- Update demande (prix/notes) ----------
  const handleUpdateDemande = async (options = { silentToast: false }) => {
    if (!selectedDemande?._id) {
      toast.error("Aucune demande sélectionnée");
      return false;
    }

    const updateData = {};
    const prixNum = Number(prix);

    if (prix && Number.isFinite(prixNum) && prixNum > 0) {
      updateData.prix = prixNum;
      // si on met un prix, la demande passe en traitement
      updateData.statut = "en_traitement";
    }

    if (commentaire !== undefined && commentaire !== null && String(commentaire).trim() !== "") {
      updateData.notes_admin = commentaire;
    }

    if (Object.keys(updateData).length === 0) {
      if (!options.silentToast) toast.error("Veuillez modifier au moins un champ avant de sauvegarder");
      return false;
    }

    try {
      const res = await axiosAuth.put(`/api/circuits-sur-mesure/${selectedDemande._id}`, updateData);

      const ok = res?.data?.success !== false; // tolérant
      if (!ok) throw new Error("Réponse invalide du serveur");

      const updated = {
        ...selectedDemande,
        ...(updateData.prix ? { prix: updateData.prix } : {}),
        ...(updateData.statut ? { statut: updateData.statut } : {}),
        ...(updateData.notes_admin ? { notes_admin: updateData.notes_admin } : {}),
      };

      setSelectedDemande(updated);
      setDemandes((prev) =>
        prev.map((d) => (d._id === selectedDemande._id ? { ...d, ...updated } : d))
      );

      if (!options.silentToast) toast.success("Informations mises à jour avec succès");
      return true;
    } catch (e) {
      console.error("Erreur lors de la mise à jour:", e);
      if (!options.silentToast) toast.error("Erreur lors de la mise à jour de la demande");
      return false;
    }
  };

  // --------- Generate CMI payment link ----------
  const generatePaymentLink = async () => {
    if (!selectedDemande?._id) {
      toast.error("Aucune demande sélectionnée");
      return;
    }

    const prixNum = Number(prix);
    if (!Number.isFinite(prixNum) || prixNum <= 0) {
      toast.error("Veuillez définir un prix valide avant de générer un lien de paiement");
      return;
    }

    try {
      setGeneratingLink(true);

      // 1) update en base (prix + statut + lien)
      const paymentUrl = computePaymentUrl(selectedDemande._id);

      const res = await axiosAuth.put(`/api/circuits-sur-mesure/${selectedDemande._id}`, {
        prix: prixNum,
        statut: "devis_envoye",
        lienPaiement: paymentUrl,
      });

      const ok = res?.data?.success !== false;
      if (!ok) throw new Error("Réponse invalide du serveur");

      // 2) update local state
      setLienPaiement(paymentUrl);

      setSelectedDemande((prev) => ({
        ...prev,
        prix: prixNum,
        lienPaiement: paymentUrl,
        statut: "devis_envoye",
      }));

      setDemandes((prev) =>
        prev.map((d) =>
          d._id === selectedDemande._id
            ? { ...d, prix: prixNum, lienPaiement: paymentUrl, statut: "devis_envoye" }
            : d
        )
      );

      // 3) email template: inject url + prix
      setEmailMessage((prev) => {
        const base = prev || buildDefaultEmail(selectedDemande, prixNum, paymentUrl);
        return base
          .replace("[LIEN_PAIEMENT]", paymentUrl)
          .replace("[PRIX À DÉFINIR]", `${prixNum.toFixed(2)} MAD`);
      });

      toast.success("Lien de paiement CMI généré avec succès");
    } catch (e) {
      console.error("Erreur lors de la génération du lien:", e);
      toast.error("Erreur lors de la génération du lien de paiement");
    } finally {
      setGeneratingLink(false);
    }
  };

  // --------- Send email ----------
  const handleSendEmail = async () => {
    if (!selectedDemande?._id) {
      toast.error("Aucune demande sélectionnée");
      return;
    }
    if (!selectedDemande?.email) {
      toast.error("Adresse email du client manquante");
      return;
    }
    if (!emailMessage || !String(emailMessage).trim()) {
      toast.error("Veuillez rédiger un message avant d'envoyer l'email");
      return;
    }
    if (!lienPaiement) {
      toast.error("Générez d'abord un lien de paiement");
      return;
    }

    try {
      setSendingEmail(true);

      let finalMessage = emailMessage;

      if (!finalMessage.includes(lienPaiement)) {
        finalMessage = finalMessage.replace("[LIEN_PAIEMENT]", lienPaiement);
      }

      const res = await axiosAuth.post("/api/circuits-sur-mesure/send-email", {
        demandeId: selectedDemande._id,
        email: selectedDemande.email,
        subject: `Votre devis pour "${selectedDemande.titre}"`,
        message: finalMessage,
      });

      const ok = res?.data?.success !== false;
      if (!ok) throw new Error("Réponse invalide du serveur");

      // si email envoyé -> statut devis_envoye si pas déjà
      if (selectedDemande.statut !== "devis_envoye") {
        setSelectedDemande((prev) => ({ ...prev, statut: "devis_envoye" }));
        setDemandes((prev) =>
          prev.map((d) => (d._id === selectedDemande._id ? { ...d, statut: "devis_envoye" } : d))
        );
      }

      toast.success("Email envoyé avec succès au client");
    } catch (e) {
      console.error("Erreur envoi email:", e);
      toast.error("Erreur lors de l'envoi de l'email");
    } finally {
      setSendingEmail(false);
    }
  };

  // --------- Copy payment link ----------
  const handleCopyLink = async () => {
    if (!lienPaiement) return;
    try {
      await navigator.clipboard.writeText(lienPaiement);
      toast.success("Lien de paiement copié dans le presse-papier");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de copier le lien");
    }
  };

  // --------- Render states ----------
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-red-500">
          <p className="text-xl font-semibold">Erreur</p>
          <p className="mt-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center">
              <span className="mr-2">🗺️</span>
              <span>Demandes de Circuits sur Mesure</span>
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {demandes.length} demande{demandes.length !== 1 ? "s" : ""} au total
            </p>
          </div>
        </div>
      </div>

      {/* Liste des demandes */}
      {demandes.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-6xl mb-4">📭</div>
          <p className="text-slate-500 text-lg mb-2">Aucune demande de circuit sur mesure</p>
          <p className="text-slate-400 text-sm">
            Les demandes s&apos;afficheront ici lorsque des clients en soumettront
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Circuit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Date de demande
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-slate-200">
              {demandes.map((demande) => (
                <tr key={demande._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-500">
                        <User size={20} />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-slate-900">
                          {demande.prenom && demande.nom
                            ? `${demande.prenom} ${demande.nom}`
                            : demande.nom_utilisateur || "Client"}
                        </div>
                        <div className="text-sm text-slate-500">{demande.email}</div>
                        {demande.telephone && (
                          <div className="text-xs text-slate-500">{demande.telephone}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 max-w-xs truncate">{demande.titre}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      <Users size={14} /> {demande.nb_personnes} pers.
                      <span className="mx-1">|</span>
                      <Clock size={14} /> {demande.duree} jours
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900">{formatDate(demande.date_creation)}</div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        getStatusLabel(demande.statut).class
                      }`}
                    >
                      {getStatusLabel(demande.statut).label}
                    </span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {/* ✅ Harmonisé MAD */}
                    {demande.prix ? `${Number(demande.prix).toLocaleString()} MAD` : "-"}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(demande)}
                      className="text-green-600 hover:text-green-900 px-3 py-1 rounded-lg border border-green-600 hover:bg-green-50 transition-all"
                    >
                      🔍 Détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedDemande && (
        <div className="fixed inset-0 z-[70] overflow-y-auto pt-20">
          <div className="flex min-h-screen items-center justify-center p-4 text-center">
            <div
              className="fixed inset-0 bg-slate-900 bg-opacity-50 transition-opacity"
              onClick={() => setShowDetailsModal(false)}
            ></div>

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[88vh]">
              {/* En-tête */}
              <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white px-7 py-5">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-white/70">Demande de circuit sur mesure</p>
                    <h3 className="text-xl font-bold mt-1">{selectedDemande.titre || "Sans titre"}</h3>
                    <div className="mt-2 flex items-center gap-2 text-xs text-white/80">
                      <span>Créée le {formatDate(selectedDemande.date_creation)}</span>
                      <span>·</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 font-semibold capitalize">{selectedDemande.statut || "Nouveau"}</span>
                    </div>
                  </div>
                  <button onClick={() => setShowDetailsModal(false)} className="text-white/80 hover:text-white text-3xl leading-none">×</button>
                </div>
              </div>

              {/* Corps : 2 colonnes */}
              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-1 lg:grid-cols-5">
                  {/* GAUCHE : infos */}
                  <div className="lg:col-span-3 p-6 space-y-6 lg:border-r border-slate-100">
                    {/* Récap voyage */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Détails du voyage</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          { label: "Date de début", value: formatDate(selectedDemande.date_debut) },
                          { label: "Durée", value: `${selectedDemande.duree} jours` },
                          { label: "Personnes", value: selectedDemande.nb_personnes },
                          { label: "Hébergement", value: selectedDemande.type_hebergement },
                          { label: "Véhicule", value: selectedDemande.type_vehicule },
                          { label: "Budget", value: selectedDemande.budget_approx ? `${selectedDemande.budget_approx} MAD` : "—" },
                        ].map((it, i) => (
                          <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                            <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">{it.label}</div>
                            <div className="text-sm font-semibold text-slate-800 mt-1 capitalize truncate">{it.value || "—"}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Description */}
                    {selectedDemande.description && (
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Description</h4>
                        <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-xl p-4 border border-slate-100">{selectedDemande.description}</p>
                      </div>
                    )}

                    {/* Itinéraire */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Itinéraire</h4>
                      <div className="space-y-2">
                        {(selectedDemande.etapes || []).map((etape, index) => (
                          <div key={index} className="flex gap-3 items-start bg-slate-50 rounded-xl p-3 border border-slate-100">
                            <div className="bg-orange-500 text-white rounded-lg w-7 h-7 flex items-center justify-center text-xs font-bold flex-none">{index + 1}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1 font-semibold text-slate-800 text-sm truncate"><MapPin size={14} className="text-orange-500 flex-none" />{etape.ville}</div>
                                <span className="text-xs text-slate-500 flex-none">{etape.duree_sejour} jour{etape.duree_sejour > 1 ? "s" : ""}</span>
                              </div>
                              {etape.activites && <p className="text-xs text-slate-600 mt-1">{etape.activites}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Demandes spécifiques */}
                    {selectedDemande.demandes_specifiques && (
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Demandes spécifiques</h4>
                        <p className="text-sm text-slate-700 bg-slate-50 rounded-xl p-4 border border-slate-100">{selectedDemande.demandes_specifiques}</p>
                      </div>
                    )}

                    {/* Contact */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Contact client</h4>
                      <div className="bg-orange-50/60 rounded-xl p-4 border border-orange-100 space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-800"><User size={15} className="text-orange-500 flex-none" /><span className="font-medium">{selectedDemande.prenom && selectedDemande.nom ? `${selectedDemande.prenom} ${selectedDemande.nom}` : selectedDemande.nom_utilisateur || "—"}</span></div>
                        <div className="flex items-center gap-2 text-slate-700 break-all"><Mail size={15} className="text-orange-500 flex-none" />{selectedDemande.email}</div>
                        <div className="flex items-center gap-2 text-slate-700"><Phone size={15} className="text-orange-500 flex-none" />{selectedDemande.telephone || "—"}</div>
                      </div>
                    </div>

                    {/* Notes admin */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1"><MessageSquare size={13} />Notes administratives (privées)</h4>
                      <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="Ajoutez des notes privées..." className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent min-h-[90px] text-sm" />
                      <button onClick={() => handleUpdateDemande()} className="mt-2 px-4 py-2 bg-slate-800 text-white text-sm font-semibold rounded-lg hover:bg-slate-900 transition-colors">Enregistrer les notes</button>
                    </div>
                  </div>

                  {/* DROITE : actions admin */}
                  <div className="lg:col-span-2 p-6 bg-slate-50/70 space-y-5">
                    {/* Tarification & paiement */}
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                      <h4 className="text-sm font-bold text-slate-900 mb-4">💳 Tarification &amp; paiement</h4>

                      <label className="block text-xs font-semibold text-slate-500 mb-1">Prix final (MAD)</label>
                      <div className="flex gap-2">
                        <input type="number" value={prix} onChange={(e) => setPrix(e.target.value)} placeholder="0" className="flex-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm" />
                        <button onClick={() => handleUpdateDemande()} className="px-3 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap">Définir</button>
                      </div>

                      <button onClick={generatePaymentLink} disabled={!prix || Number(prix) <= 0 || generatingLink} className="mt-4 w-full px-4 py-3 bg-green-600 text-white text-sm font-bold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {generatingLink ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Génération...</>) : (<><Send size={16} />Générer le lien de paiement</>)}
                      </button>

                      {lienPaiement && (
                        <div className="mt-4">
                          <label className="block text-xs font-semibold text-slate-500 mb-1">Lien de paiement</label>
                          <div className="flex">
                            <input type="text" value={lienPaiement} readOnly className="flex-1 min-w-0 px-3 py-2 border border-slate-200 rounded-l-lg bg-slate-50 text-xs text-slate-600 truncate" />
                            <button onClick={handleCopyLink} className="px-3 bg-slate-100 border border-l-0 border-slate-200 rounded-r-lg hover:bg-slate-200 transition-colors flex-none"><Copy size={16} /></button>
                          </div>
                          <a href={lienPaiement} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-block text-xs font-semibold text-orange-600 hover:underline">Ouvrir le lien ↗</a>
                        </div>
                      )}
                    </div>

                    {/* Email */}
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                      <h4 className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-2"><Mail size={16} className="text-green-600" />Envoyer le devis par email</h4>
                      <p className="text-xs text-slate-400 mb-3 break-all">À&nbsp;: <strong className="text-slate-600">{selectedDemande.email}</strong></p>
                      <textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} placeholder="Rédigez votre message..." className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent min-h-[150px] text-xs" />
                      <p className="text-[11px] text-slate-400 mt-1">Astuce&nbsp;: <code>[LIEN_PAIEMENT]</code> insère automatiquement le lien.</p>
                      <button onClick={handleSendEmail} disabled={sendingEmail || !lienPaiement} className="mt-3 w-full px-4 py-3 bg-green-600 text-white text-sm font-bold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {sendingEmail ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Envoi...</>) : (<><Send size={16} />Envoyer l&apos;email</>)}
                      </button>
                      {!lienPaiement && <p className="mt-2 text-xs text-red-500">Générez d&apos;abord un lien de paiement.</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-white border-t border-slate-100 px-6 py-3 flex justify-end">
                <button onClick={() => setShowDetailsModal(false)} className="px-5 py-2 border border-slate-300 rounded-lg text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CircuitSurMesureManager;
